import React, { useEffect, useState, useCallback, useRef, createContext, useContext } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { _setToastHandler, notify } from '../utils/notificationService';

// Components may grab `notify` via the hook, but it's also importable directly
// from the service for use outside the React tree (socket handlers, etc.).
const NotifyContext = createContext(notify);
export const useNotify = () => useContext(NotifyContext);

// Renders the single, app-wide toast. Toasts are queued so a burst (e.g. several
// broadcast/template events) is shown one after another rather than clobbering.
export const NotificationProvider = ({ children }) => {
  const queueRef = useRef([]);
  const [current, setCurrent] = useState(null);

  const pump = useCallback(() => {
    setCurrent((cur) => (cur ? cur : (queueRef.current.shift() || null)));
  }, []);

  useEffect(() => {
    _setToastHandler((toast) => {
      queueRef.current.push(toast);
      pump();
    });
    return () => _setToastHandler(null);
  }, [pump]);

  const handleClose = useCallback((_e, reason) => {
    if (reason === 'clickaway') return;
    setCurrent(null);
  }, []);

  // After the exit animation, show the next queued toast (if any).
  const handleExited = useCallback(() => {
    setCurrent(queueRef.current.shift() || null);
  }, []);

  return (
    <NotifyContext.Provider value={notify}>
      {children}
      <Snackbar
        key={current?.key}
        open={!!current}
        autoHideDuration={current?.duration || 4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionProps={{ onExited: handleExited }}
      >
        <Alert
          onClose={handleClose}
          severity={current?.severity || 'info'}
          variant="filled"
          sx={{ width: '100%', boxShadow: 6 }}
        >
          {current?.message}
        </Alert>
      </Snackbar>
    </NotifyContext.Provider>
  );
};

export default NotificationProvider;
